import React, { useRef, useState, useCallback, useMemo } from 'react'

const DEFAULT_WIDTH = 320
const MIN_WIDTH = 260
const MAX_WIDTH = 800
const MIN_HEIGHT = 360

const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

const pick = (obj, keys) => {
  for (const key of keys) {
    const v = obj?.[key]
    if (v !== undefined && v !== null && String(v).trim() !== '') return v
  }
  return ''
}

const formatNumber = (v) => {
  if (v === '' || v === null || v === undefined) return ''
  const n = Number(String(v).replace(/,/g, ''))
  return Number.isFinite(n) ? n.toLocaleString('ja-JP') : v
}

const displayBranchCode = (value) =>
  typeof value === 'string' && value.trim().toLowerCase() === 'none'
    ? ''
    : value

// Fields of input_data.data.customers. Labels without keys have no source data yet.
const REFERENCE_FIELDS = [
  { label: '産地', keys: ['production'] },
  { label: '出荷者', keys: [] },
  { label: '等級', keys: ['grade'] },
  { label: '階級', keys: ['size'] },
  { label: '量目', keys: ['scale'], number: true },
  { label: '荷姿', keys: ['package'] },
  { label: '入数', keys: [] },
  { label: '提案価格', keys: ['price'], number: true },
  { label: '日備考', keys: ['daily_note', 'DAILY_NOTE'], multiline: true },
  { label: '週備考', keys: ['weekly_note', 'WEEKLY_NOTE'], multiline: true },
  { label: '全備考', keys: ['note', 'NOTE'], multiline: true }
]

const buildOrderRows = (customers) =>
  (Array.isArray(customers) ? customers : []).map((c, idx) => ({
    ...c,
    hic: pick(c, ['meisei_hinmei_cd']),
    customerCode: pick(c, ['customer_code']),
    branchCode: displayBranchCode(pick(c, ['branch_code'])),
    orderQuantity: pick(c, ['order_quantity']),
    // No field combination is unique (order_num+detail_num repeats across branches), so include idx.
    key: `${pick(c, ['order_num'])}::${pick(c, ['detail_num'])}::${idx}`
  }))

const OrderPanel = ({ customers, isOpen, productNameByHic = {} }) => {
  const [selectedKey, setSelectedKey] = useState(null)
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: null })
  const panelRef = useRef(null)

  const rows = useMemo(() => buildOrderRows(customers), [customers])
  const selectedRow =
    rows.find((r) => r.key === selectedKey) || rows[0] || null

  const startResize = useCallback((e, axis) => {
    const panel = panelRef.current
    if (!panel) return
    e.preventDefault()
    e.stopPropagation()

    const rect = panel.getBoundingClientRect()
    const maxHeight = panel.parentElement?.clientHeight || window.innerHeight
    const startX = e.clientX
    const startY = e.clientY
    const cursor =
      axis === 'x' ? 'ew-resize' : axis === 'y' ? 'ns-resize' : 'nesw-resize'

    document.body.style.cursor = cursor
    document.body.style.userSelect = 'none'

    // Panel sits on the right edge, so dragging left must grow the width.
    const onMove = (ev) => {
      setSize((prev) => ({
        width:
          axis === 'y'
            ? prev.width
            : clamp(rect.width - (ev.clientX - startX), MIN_WIDTH, MAX_WIDTH),
        height:
          axis === 'x'
            ? prev.height
            : clamp(rect.height + (ev.clientY - startY), MIN_HEIGHT, maxHeight)
      }))
    }

    const onUp = () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  if (!isOpen) return null

  return (
      <div
        ref={panelRef}
        className='rp-order-panel'
        style={{
          width: size.width,
          flexBasis: size.width,
          height: size.height ?? undefined,
          alignSelf: size.height ? 'flex-start' : undefined
        }}
      >
        <div
          className='rp-order-panel__resize rp-order-panel__resize--x'
          onPointerDown={(e) => startResize(e, 'x')}
        />
        <div
          className='rp-order-panel__resize rp-order-panel__resize--y'
          onPointerDown={(e) => startResize(e, 'y')}
        />
        <div
          className='rp-order-panel__resize rp-order-panel__resize--xy'
          onPointerDown={(e) => startResize(e, 'xy')}
        />
        <div className='rp-order-panel__body'>
          <div className='rp-order-panel__table-wrap'>
            <table className='rp-order-panel__table'>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '14%' }} />
                <col />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={2}>買受人</th>
                  <th>品名</th>
                  <th>受注数</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className='rp-order-panel__empty'>
                      受注データはありません
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.key}
                      className={
                        selectedRow?.key === row.key
                          ? 'rp-order-panel__row--selected'
                          : ''
                      }
                      onClick={() => setSelectedKey(row.key)}
                    >
                      <td>{row.customerCode}</td>
                      <td>{row.branchCode}</td>
                      <td className='rp-order-panel__name'>
                        {productNameByHic[row.hic] || row.hic || ''}
                      </td>
                      <td className='rp-order-panel__qty'>
                        {formatNumber(row.orderQuantity)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className='rp-order-panel__reference'>
            <div className='rp-order-panel__reference-title'>＜参考情報＞</div>
            {REFERENCE_FIELDS.map(({ label, keys, number, multiline }) => {
              const raw = selectedRow ? pick(selectedRow, keys) : ''
              return (
                <div
                  key={label}
                  className={`rp-order-panel__reference-row${multiline ? ' rp-order-panel__reference-row--multiline' : ''}`}
                >
                  <span>{label}</span>
                  <span className='rp-order-panel__reference-value'>
                    {number ? formatNumber(raw) : raw}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
  )
}

export default React.memo(OrderPanel)
